'use client';

import { createContext, useContext, useEffect, useState } from 'react';
import { useRouter } from 'next/navigation';
import { auth, loginWithEmailAndPassword, logoutUser, onAuthStateChanged } from '@/lib/config/firebase';
import { setAuthCookie, removeAuthCookie } from '@/lib/utils/cookieUtils';

const AuthContext = createContext({});

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const router = useRouter();

  // Listen for auth state changes
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (authUser) => {
      if (authUser) {
        setUser(authUser);
        // Set auth cookie when user is authenticated
        authUser.getIdToken().then(token => {
          setAuthCookie(token);
        });
      } else {
        setUser(null);
        // Remove auth cookie when user is not authenticated
        removeAuthCookie();
      }
      setLoading(false);
    });

    // Cleanup subscription
    return () => unsubscribe();
  }, []);

  // Login function
  const login = async (email, password) => {
    setLoading(true);
    const result = await loginWithEmailAndPassword(email, password);
    setLoading(false);
    
    if (result.user) {
      router.push('/check-in');
      return { success: true };
    }
    
    return { success: false, error: result.error };
  };

  // Logout function
  const logout = async () => {
    setLoading(true);
    const result = await logoutUser();
    
    // Remove auth cookie regardless of logout success
    removeAuthCookie();
    
    setLoading(false);
    
    if (result.success) {
      // Use router.replace instead of push to prevent going back with browser history
      router.replace('/');
      return { success: true };
    }
    
    return { success: false, error: result.error };
  };

  const value = {
    user,
    loading,
    login,
    logout,
    isAuthenticated: !!user,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};